export type Locale = "en" | "zh";

export const translations = {
  en: {
    common: {
      brand: "Groweb Analytics",
      loading: "Loading…",
      workspaces: "Workspaces",
      accountSecurity: "Account Security",
      logout: "Sign Out",
      back: "Back",
      submit: "Submit",
      submitting: "Submitting…",
      create: "Create",
      creating: "Creating…",
      cancel: "Cancel",
      save: "Save",
      delete: "Delete",
      refresh: "Refresh",
      copy: "Copy",
      copied: "Copied!",
      error: "Error",
      success: "Success",
      actions: "Actions",
      status: "Status",
      name: "Name",
      email: "Email",
      role: "Role",
      open: "Open",
      active: "Active",
      language: "Language",
    },
    auth: {
      loginTitle: "Sign In",
      registerTitle: "Create Account",
      resetTitle: "Reset Password",
      mfaTitle: "Two-Factor Authentication (MFA)",
      mfaDesc: "Two-factor authentication is enabled. Please enter your code.",
      totpLabel: "6-Digit Authenticator Code (TOTP)",
      totpPlaceholder: "123456",
      recoveryLabel: "Emergency Recovery Code",
      recoveryPlaceholder: "ABCD-EFGH",
      useRecovery: "Lost authenticator? Use emergency recovery code",
      useTotp: "Switch back to 6-digit TOTP code",
      verifyAndLogin: "Verify & Sign In",
      cancelRetry: "Cancel & Retry",
      emailLabel: "Email",
      passwordLabel: "Password",
      newPasswordLabel: "New Password (≥12 chars)",
      confirmPasswordLabel: "Confirm Password",
      passwordMismatch: "Passwords do not match",
      sendEmail: "Send Email",
      toRegister: "Need an account? Register",
      toLogin: "Already have an account? Sign In",
      forgotPassword: "Forgot password?",
      backToLogin: "Back to Sign In",
      registerSent: "If the email is valid, a verification link has been sent.",
      resetSent: "If the account exists, password reset instructions have been sent.",
      verifyingEmail: "Verifying email address…",
      verifySuccess: "Email verified successfully. Please sign in.",
      alreadyVerified: "Email already verified.",
      verifyFailed: "Verification failed: ",
      resetPasswordHeading: "Reset Password",
      missingResetToken: "Missing reset token. Please use the link sent to your email.",
      resetSuccess: "Password reset successfully. Previous sessions revoked. Please log in with your new password.",
      inviteHeading: "Workspace Invitation",
      acceptingInvite: "Accepting invitation…",
      invalidInviteLink: "Invalid invitation link (missing token or workspace).",
      joinedWorkspace: "Successfully joined workspace. Current user: ",
      needLoginToAccept: "Please sign in or register with the invited email address first, then reopen this link.",
    },
    workspaces: {
      title: "My Workspaces",
      empty: "No workspaces created yet.",
      tableHeaderName: "Name",
      tableHeaderCapabilities: "Capabilities",
      tableHeaderAction: "Action",
      createTitle: "Create Workspace (1 per account on current plan)",
      namePlaceholder: "Workspace Name",
    },
    workspaceDetail: {
      planLabel: "Plan: ",
      statusLabel: "Status: ",
      usageTitle: "Usage & Quota (Current Period)",
      usedAu: "Used AU",
      remainingAu: "Remaining AU",
      pvReserved: "PV Protected Pool",
      originAu: "Origin AU",
      pvAu: "PV AU",
      customAu: "Custom AU",
      periodLabel: "Period",
      usageNotice: "Remaining capacity is workspace-wide. Origin/Custom usage is constrained by page protection pools and site quotas.",
      noPeriod: "No active billing period.",
      sitesSection: "Sites",
      noSites: "No sites added yet. Add a site to start collecting traffic.",
      addSiteButton: "Add Site",
      siteNamePlaceholder: "Site Name (e.g. My SaaS Landing)",
      createSiteButton: "Create Site",
      domainVerificationBadge: "Domain Verification: ",
      domainPending: "Pending DNS verification",
      domainVerified: "Domain Verified",
      verifyDomainBtn: "Verify DNS TXT",
      membersSection: "Team Members",
      inviteMemberButton: "Invite Member",
      memberEmailPlaceholder: "colleague@example.com",
      roleOwner: "Owner",
      roleAdmin: "Admin",
      roleMember: "Member",
      roleAnalyst: "Analyst",
      sendInviteButton: "Send Invitation",
      removeMemberConfirm: "Are you sure you want to remove this member?",
      dangerZone: "Danger Zone",
      transferOwnership: "Transfer Workspace Ownership",
      transferRecipientPlaceholder: "New owner email address",
      reauthPasswordPlaceholder: "Confirm with your password",
      transferButton: "Transfer Ownership",
      deleteWorkspace: "Delete Workspace",
      deleteWarning: "This permanently deletes all sites, traffic events, and workspace history. Action cannot be undone.",
      deleteButton: "Permanently Delete Workspace",
      setupWizardTitle: "Setup Wizard",
      step1CreateSite: "① Create Site",
      step2RequestDns: "② Request DNS Challenge",
      step3VerifyDns: "③ Verify",
      step4EnableBrowser: "④ Enable Browser Source",
      step5EnableOrigin: "⑤ Enable Origin Agent",
      txtRecordName: "TXT Name: ",
      txtRecordValue: "TXT Value: ",
      dnsTip: "Production: Add the TXT record at your DNS provider and click Verify. Demo: Pre-provisioned domains pass immediately.",
      openDemoPage: "Open Demo Page (Simulate Customer Site)",
      agentKeyHint: "Agent Key (displayed once): ",
      inviteViewer: "Invite Viewer",
      inviteCreated: "Invitation created (check API logs in dev).",
      removeMember: "Remove",
      dissolveWorkspace: "Dissolve This Workspace",
      dissolveNotice: "Dissolving a workspace permanently halts data collection, terminates active subscriptions, and cleans up all associated sites.",
      transferConfirmModalTitle: "Transfer Workspace Ownership",
      transferPrompt: "Are you sure you want to transfer ownership to ",
      transferDemoteWarning: " You will be demoted to an admin.",
      deleteConfirmModalTitle: "Dissolve Workspace",
      deleteWarningModal: "Warning: This action is irreversible! All collection, active subscriptions, and historical analytics will be terminated.",
      confirmDissolveBtn: "Confirm Permanent Deletion",
      cancelSubscriptionBtn: "Cancel Subscription (Revert to Free)",
      cancelSubscriptionConfirmTitle: "Cancel Subscription",
      cancelSubscriptionConfirmPrompt: "Are you sure you want to cancel this paid subscription and revert this workspace to the Free tier?",
      cancelSubscriptionSuccess: "Subscription cancelled successfully. Workspace reverted to Free tier.",
      deleteSiteBtn: "Delete",
      deleteSiteConfirmTitle: "Delete Site",
      deleteSiteConfirmPrompt: "Are you sure you want to delete this site? Tracking keys will be revoked and data collection will be halted immediately.",
      deleteSiteSuccess: "Site deleted successfully.",
    },
    siteDetail: {
      analyticsTab: "📊 Analytics Dashboard",
      sourcesTab: "🔌 Data Sources & Integration",
      backToWorkspace: "← Back to Workspace",
      modeNotice: "Browser and Origin are two independent facts and are not combined into a single total count.",
      rangeToday: "Today",
      range24h: "Past 24 Hours",
      range7d: "Past 7 Days",
      range30d: "Past 30 Days",
      realtimeLiveTitle: "Live Active Visitors (Past 5 min)",
      realtimeLiveBadge: "LIVE (SSE)",
      activeVisitorsCount: "active visitors now",
      noRecentVisits: "No recent real-time visits",
      sessionsCardTitle: "Sessions & Bounce Analysis",
      totalSessions: "Total Sessions",
      uniqueVisitors: "Unique Visitors (UV)",
      bounceRate: "Bounce Rate",
      avgPages: "Pages / Session",
      pagesUnit: "pages",
      avgDuration: "Avg Session Duration",
      secondsUnit: "sec",
      noSessionData: "No session data in this period",
      sessionSplitNote: "30-min inactivity splits sessions. Single pageview with no exit event is categorized as bounced.",
      browserTrafficTitle: "Browser Traffic (PV & Unique Visitors)",
      totalPv: "Total Pageviews (PV)",
      pvUvRatio: "Avg PV / UV Depth",
      originRequestsTitle: "Origin Requests",
      totalOriginRequests: "Total Origin Requests",
      htmlDocsNotice: "of which HTML documents",
      originNotice: "Origin requests routed within selected profile, excluding edge cache hits.",
      topCountriesTitle: "Top Countries & Regions",
      topCountriesNote: "Auto-resolved via Cloudflare / edge GeoIP; __unknown__ = local / intranet.",
      topReferrersTitle: "Top Referral Sources (Browser Referrer)",
      topReferrersNote: "__unknown__ = direct navigation / no external referrer; sanitized on ingestion.",
      topPathsTitle: "Top Server Paths (Raw URI)",
      topLandingPagesTitle: "Top Landing Pages",
      landingPagesNote: "First valid pageview path within a 30-minute session window.",
      trafficChannelsTitle: "Traffic Channel Distribution",
      channelsNote: "Attributed via UTM campaign parameters, search engine databases, and AI referral patterns.",
      aiTrafficTitle: "Top Generative AI Traffic (GEO / AI Referrals)",
      aiTrafficNote: "Referrals and click-throughs from ChatGPT, Perplexity, Claude, and generative AI engines.",
      devicesTitle: "Client Environment (Devices & Browsers)",
      deviceType: "Device Type",
      browserFamily: "Browser Family",
      devicesNote: "Lightweight, server-side User-Agent parsing. Zero third-party cookies or fingerprinting.",
      botsTitle: "Search Engine & Bot Activity",
      totalBotRequests: "Total Bot Requests: ",
      topBots: "Top Crawlers",
      topCrawledPages: "Top Crawled Pages",
      noBotData: "No bot activity recorded",
      botsNote: "Monitors Googlebot, Bingbot, GPTBot, ClaudeBot, and other indexing crawlers.",
      rumTitle: "Real User Monitoring (RUM p75 Approx)",
      rumGood: "Good",
      rumNeedsImprovement: "Needs Improvement",
      rumPoor: "Poor",
      rumNoSamples: "No samples (does not mean 0ms)",
      rumLowSample: "Low sample size (<30)",
      rumNote: "p75 calculated from fixed-bucket histogram approximation; grades reflect exact threshold counts.",
      conversionsTitle: "Conversions (24h Window, Qualified Pageview Criteria)",
      noQualifiedPv: "No qualified PV",
      exportBtn: "Export Data",
      exportModalTitle: "Export Site Analytics Data",
      exportDataType: "Data Type:",
      exportOptionEvents: "Raw Event Stream (Events - up to 100k rows)",
      exportOptionMetrics: "Pre-aggregated Rollups (Metrics)",
      exportFormatLabel: "File Format:",
      exportCsvSafeNotice: "Security notice: CSV output automatically sanitizes cells starting with =, +, -, @, \\t, \\r with a single quote prefix to prevent spreadsheet formula injection.",
      exportGenerating: "Generating export file…",
      exportStartBtn: "Generate Export File",
      exportReady: "Export File Ready",
      exportJobId: "Job ID: ",
      exportToken: "Download Token: ",
      exportExpires: "Download link valid for 24 hours.",
      downloadFileBtn: "Download File Now",
      closeBtn: "Close",
      retry: "Retry",
      reload: "Reload",
      ruleVersion: "Rule v",
      pendingAggregation: "Pending aggregation: ",
      watermark: "Watermark: ",
      provisional: "Provisional (within 96h window)",
      collectionMode: "Mode: ",
      channelsLabel: "Channels: ",
      agentStatus: "Recent Agent Status: ",
      knownGaps: "Known gaps: ",
      unknownLocation: "Unknown / Local Network",
      emptyChart: "No data in selected timeframe.",
      qualityCoverage: "Data Coverage: ",
      qualityNeverCollected: "Never collected yet (zero counts indicate no active source)",
      dangerZoneTitle: "Danger Zone",
      deleteSiteNotice: "Deleting this site will immediately revoke all public tracking keys and stop data collection and analytics queries.",
      deleteSiteBtn: "Delete This Site",
      deleteSiteConfirmTitle: "Delete Site",
      deleteSiteConfirmPrompt: "Are you sure you want to permanently delete this site? Data collection and queries will be halted immediately.",
      deleteSiteSuccess: "Site deleted successfully.",
    },
    guide: {
      title: "Website Tracking Integration Guide",
      subtitle: "Universal async tracking snippet & one-click structured prompts for AI coding assistants.",
      tabHuman: "📖 Developer Guide (Manual)",
      tabAi: "🤖 AI Coding Prompt (One-Click)",
      copyCode: "📋 Copy Code",
      copiedCode: "✅ Copied to Clipboard",
      generateKey: "⚡ Generate Browser Tracking Key",
      generatingKey: "Generating…",
      refreshKey: "🔄 Refresh",
      siteKeyLabel: "Public Site Key: ",
      keyStatusGenerated: "Active",
      keyStatusPlaceholder: "Pending Generation (Placeholder)",
      step1Title: "1. Quick Embed (Single-line script in <head>)",
      step2Title: "2. Framework Recipes & Nginx Setup",
      step3Title: "3. Architectural & Safety Guarantees",
      step4Title: "4. Verification & Testing",
      feature1Title: "⚡ 100% Asynchronous & Non-blocking",
      feature1Desc: "Loaded via defer attribute and reported through sendBeacon / fetch keepalive. Never halts main thread execution or harms Core Web Vitals.",
      feature2Title: "🛡️ Nginx First-Party Proxy Friendly",
      feature2Desc: "Supports reverse proxy passthrough to ensure 100% same-origin delivery, bypass ad-blocker false positives, and isolate visitor cookies.",
      feature3Title: "🚀 Automatic SPA Route Interception",
      feature3Desc: "Seamlessly intercepts pushState, replaceState, and popstate. Tracks single-page app views and session engagement out of the box.",
      feature4Title: "📊 Field Core Web Vitals",
      feature4Desc: "Directly records real-user performance metrics including LCP, INP, CLS, FCP, and TTFB from real visitors worldwide.",
      aiBannerTitle: "🤖 One-Click AI Coding Integration Prompt",
      aiBannerDesc: "Specially crafted for Cursor (Composer / Agent), Claude Code, GitHub Copilot, and Windsurf. Copy and paste to automatically identify project framework and wire up typed analytics.",
      copyAiPromptBtn: "📋 Copy Structured AI Prompt",
      aiPreviewHeader: "AI Prompt Preview (prefilled with your actual Site Key & API endpoint)",
    },
    account: {
      title: "Account Security & Profile",
      emailLabel: "Registered Email: ",
      emailVerified: "Verified",
      emailUnverified: "Unverified",
      mfaSectionTitle: "Two-Factor Authentication (MFA / TOTP)",
      mfaStatusEnabled: "Enabled",
      mfaStatusDisabled: "Disabled",
      mfaEnableBtn: "Enable Two-Factor Authentication",
      mfaDisableBtn: "Disable Two-Factor Authentication",
      mfaScanQr: "Scan the QR code with Google Authenticator or 1Password:",
      mfaSecretLabel: "Manual Entry Key: ",
      mfaConfirmCodeLabel: "Enter 6-digit confirmation code from app:",
      mfaConfirmBtn: "Confirm & Activate MFA",
      mfaRecoveryCodesTitle: "Emergency Recovery Codes (Save these securely):",
      deleteSectionTitle: "Delete Account",
      deleteWarning: "Account deletion is irreversible. If you own workspaces, transfer ownership or delete them first.",
      deleteBtn: "Delete My Account",
      changePasswordTitle: "Change Password",
      changePasswordDesc: "Update your password to keep your account secure.",
      currentPasswordLabel: "Current Password",
      newPasswordLabel: "New Password (≥12 characters)",
      confirmNewPasswordLabel: "Confirm New Password",
      changePasswordBtn: "Update Password",
      changePasswordSuccess: "Password updated successfully. Other active sessions have been signed out.",
      passwordMismatch: "New password and confirmation do not match.",
      passwordSameAsCurrent: "New password cannot be the same as the current password.",
    },
  },
  zh: {
    common: {
      brand: "Groweb 流量分析",
      loading: "加载中…",
      workspaces: "工作空间",
      accountSecurity: "账户安全",
      logout: "退出登录",
      back: "返回",
      submit: "提交",
      submitting: "提交中…",
      create: "创建",
      creating: "创建中…",
      cancel: "取消",
      save: "保存",
      delete: "删除",
      refresh: "刷新",
      copy: "复制",
      copied: "已复制！",
      error: "错误",
      success: "成功",
      actions: "操作",
      status: "状态",
      name: "名称",
      email: "邮箱",
      role: "角色",
      open: "打开",
      active: "生效中",
      language: "语言",
    },
    auth: {
      loginTitle: "系统登录",
      registerTitle: "注册新账号",
      resetTitle: "重置密码",
      mfaTitle: "两步验证 (MFA)",
      mfaDesc: "当前账号已启用两步验证，请提供动态验证码。",
      totpLabel: "6 位动态验证码 (TOTP)",
      totpPlaceholder: "123456",
      recoveryLabel: "应急恢复码",
      recoveryPlaceholder: "ABCD-EFGH",
      useRecovery: "丢失 Authenticator？使用应急恢复码",
      useTotp: "返回使用 6 位动态验证码",
      verifyAndLogin: "验证并登录",
      cancelRetry: "取消重试",
      emailLabel: "邮箱",
      passwordLabel: "密码",
      newPasswordLabel: "新密码(≥12 字符)",
      confirmPasswordLabel: "再次输入密码",
      passwordMismatch: "两次输入的密码不一致",
      sendEmail: "发送邮件",
      toRegister: "去注册账号",
      toLogin: "去登录系统",
      forgotPassword: "忘记密码？",
      backToLogin: "返回登录",
      registerSent: "若邮箱可用，验证邮件已发送（开发环境在日志中查看）。",
      resetSent: "若邮箱存在，密码重置邮件已发送。",
      verifyingEmail: "正在验证邮箱…",
      verifySuccess: "邮箱验证成功，请登录。",
      alreadyVerified: "邮箱已验证（重复确认）。",
      verifyFailed: "验证失败: ",
      resetPasswordHeading: "重置密码",
      missingResetToken: "缺少重置令牌，请从邮件链接进入。",
      resetSuccess: "密码已重置，旧会话已全部撤销。请使用新密码登录。",
      inviteHeading: "工作空间邀请",
      acceptingInvite: "正在接受邀请…",
      invalidInviteLink: "邀请链接无效（缺少令牌或空间参数）。",
      joinedWorkspace: "已成功加入工作空间。当前账号: ",
      needLoginToAccept: "请先注册或登录被邀请的邮箱账号，再重新打开本链接。",
    },
    workspaces: {
      title: "我的工作空间",
      empty: "还没有工作空间。",
      tableHeaderName: "名称",
      tableHeaderCapabilities: "能力权限",
      tableHeaderAction: "操作",
      createTitle: "创建工作空间 (当前套餐每账户 1 个)",
      namePlaceholder: "空间名称",
    },
    workspaceDetail: {
      planLabel: "套餐: ",
      statusLabel: "状态: ",
      usageTitle: "用量与额度 (当期)",
      usedAu: "已用 AU",
      remainingAu: "剩余 AU",
      pvReserved: "Pageview 保护量",
      originAu: "源站 AU",
      pvAu: "PV AU",
      customAu: "自定义 AU",
      periodLabel: "账期",
      usageNotice: "剩余额度为空间总量，源站/自定义另受页面保护池与站点上限约束。",
      noPeriod: "无当期账期。",
      sitesSection: "站点列表",
      noSites: "暂未添加任何站点。添加站点即可开始采集网站流量数据。",
      addSiteButton: "添加新站点",
      siteNamePlaceholder: "站点名称 (例如: 官网主站)",
      createSiteButton: "确认创建",
      domainVerificationBadge: "域名所有权: ",
      domainPending: "等待 DNS TXT 解析验证",
      domainVerified: "域名已验证",
      verifyDomainBtn: "验证 DNS TXT 记录",
      membersSection: "团队成员",
      inviteMemberButton: "邀请成员",
      memberEmailPlaceholder: "colleague@example.com",
      roleOwner: "所有者 (Owner)",
      roleAdmin: "管理员 (Admin)",
      roleMember: "普通成员 (Member)",
      roleAnalyst: "分析师 (Analyst)",
      sendInviteButton: "发送邀请链接",
      removeMemberConfirm: "确定要移除该成员吗？",
      dangerZone: "危险区域",
      transferOwnership: "转移工作空间所有权",
      transferRecipientPlaceholder: "新所有者邮箱",
      reauthPasswordPlaceholder: "请输入您的密码确认",
      transferButton: "转移所有权",
      deleteWorkspace: "删除工作空间",
      deleteWarning: "删除后所有站点配置、埋点事件及分析数据将被永久物理销毁，不可恢复。",
      deleteButton: "永久删除工作空间",
      setupWizardTitle: "接入向导",
      step1CreateSite: "① 创建站点",
      step2RequestDns: "② 申请域名验证",
      step3VerifyDns: "③ 验证",
      step4EnableBrowser: "④ 启用浏览器来源",
      step5EnableOrigin: "⑤ 启用源站 Agent",
      txtRecordName: "TXT 名称: ",
      txtRecordValue: "TXT 值: ",
      dnsTip: "生产:到 DNS 服务商添加该记录后点'③ 验证'。本地演示:运维已预置的域名直接点验证即可。",
      openDemoPage: "打开演示页面(模拟客户网站)",
      agentKeyHint: "Agent 密钥(仅此一次显示): ",
      inviteViewer: "邀请 viewer",
      inviteCreated: "邀请已创建(开发环境链接在 API 日志)。",
      removeMember: "移除",
      dissolveWorkspace: "解散此工作空间",
      dissolveNotice: "解散工作空间将永久终止数据采集、清理所有关联站点，并自动注销该空间名下的有效付费订阅。",
      transferConfirmModalTitle: "转让空间所有权",
      transferPrompt: "确定将工作空间所有权转让给 ",
      transferDemoteWarning: " 吗？转让后您将降级为管理员。",
      deleteConfirmModalTitle: "解散工作空间",
      deleteWarningModal: "警告：此操作不可撤销！空间下所有站点采集、有效付费订阅及历史数据将一并被注销销毁。",
      confirmDissolveBtn: "确认永久解散",
      cancelSubscriptionBtn: "取消订阅（降级为免费版）",
      cancelSubscriptionConfirmTitle: "取消付费订阅",
      cancelSubscriptionConfirmPrompt: "确定要取消此工作空间的付费订阅并降级为免费版吗？操作后额度将恢复为免费版额度。",
      cancelSubscriptionSuccess: "订阅已成功取消，当前工作空间已降级为免费版。",
      deleteSiteBtn: "删除",
      deleteSiteConfirmTitle: "删除站点",
      deleteSiteConfirmPrompt: "确定要删除此站点吗？删除后所有上报密钥将失效，数据采集将立即停止。",
      deleteSiteSuccess: "站点已成功删除。",
    },
    siteDetail: {
      analyticsTab: "📊 数据分析看板",
      sourcesTab: "🔌 数据来源 (Sources) & 接入指引",
      backToWorkspace: "← 返回工作空间",
      modeNotice: "浏览器与源站是两套独立事实，不做合并\"总访问量\"。",
      rangeToday: "今日",
      range24h: "最近24小时",
      range7d: "近7天",
      range30d: "近30天",
      realtimeLiveTitle: "在线实时访客 (最近 5 分钟)",
      realtimeLiveBadge: "LIVE (SSE)",
      activeVisitorsCount: "当前活跃访客",
      noRecentVisits: "最近无实时访问",
      sessionsCardTitle: "会话分析与跳出率 (v3.1)",
      totalSessions: "总会话数",
      uniqueVisitors: "独立访客 (UV)",
      bounceRate: "跳出率 (Bounce Rate)",
      avgPages: "平均会话浏览",
      pagesUnit: "页",
      avgDuration: "平均会话时长",
      secondsUnit: "秒",
      noSessionData: "暂无会话数据",
      sessionSplitNote: "30分钟不活动自动切分会话；单页浏览无后续判定为跳出。",
      browserTrafficTitle: "浏览器流量 (PV 与独立访客 UV)",
      totalPv: "总浏览量 (PV)",
      pvUvRatio: "人均浏览深度 (PV/UV)",
      originRequestsTitle: "源站请求 (Origin Requests)",
      totalOriginRequests: "总回源请求数",
      htmlDocsNotice: "其中 HTML 文档",
      originNotice: "口径:所选 profile 内回源请求，非全边缘。",
      topCountriesTitle: "地理区域 Top (国家与地区分布)",
      topCountriesNote: "基于 Cloudflare / 反向代理 GeoIP 自动识别；__unknown__ = 未知或内网 IP。",
      topReferrersTitle: "入口来源 Top (浏览器 referrer)",
      topReferrersNote: "__unknown__ = 无外部入口/直连; referrer 在接受时清洗保存。",
      topPathsTitle: "源站路径 Top (raw 精确)",
      topLandingPagesTitle: "会话入口落地页 Top (Landing Pages)",
      landingPagesNote: "基于 30 分钟窗口会话的首个有效 Pageview 路径。",
      trafficChannelsTitle: "流量渠道分布 (Traffic Channels)",
      channelsNote: "根据 UTM 参数、搜索引擎库与 AI 来源自动归因分类。",
      aiTrafficTitle: "AI 引擎流量 Top (GEO / Generative Engine)",
      aiTrafficNote: "来自 ChatGPT, Perplexity, Claude 等生成式 AI 平台的推荐点击。",
      devicesTitle: "访问设备与浏览器 (Client Environment)",
      deviceType: "设备类型",
      browserFamily: "浏览器家族",
      devicesNote: "服务端轻量 User-Agent 分析，保护隐私且无需三方 Cookie。",
      botsTitle: "搜索引擎与爬虫监控 (Bot / Crawler Activity)",
      totalBotRequests: "总爬取请求: ",
      topBots: "爬虫身份 Top",
      topCrawledPages: "抓取页面 Top",
      noBotData: "无爬虫数据",
      botsNote: "识别 Googlebot, Bingbot, GPTBot, ClaudeBot 等爬虫行为。",
      rumTitle: "页面体验 RUM (p75 近似)",
      rumGood: "良好",
      rumNeedsImprovement: "需改善",
      rumPoor: "较差",
      rumNoSamples: "无样本(不等于 0ms)",
      rumLowSample: "低样本(<30)",
      rumNote: "p75 来自固定边界直方图桶，近似值；各档比例为精确阈值计数。",
      conversionsTitle: "转化 (24h，合格父页面口径)",
      noQualifiedPv: "无合格 PV",
      exportBtn: "导出数据",
      exportModalTitle: "导出站点数据",
      exportDataType: "数据类型：",
      exportOptionEvents: "原始事件明细 (Events - 最多 10 万行)",
      exportOptionMetrics: "预聚合指标 (Metrics Rollups)",
      exportFormatLabel: "导出文件格式：",
      exportCsvSafeNotice: "安全提示：CSV 格式会自动对首字符为 =, +, -, @, \\t, \\r 的单元格进行单引号前缀转义，防止 Excel/WPS 公式注入。",
      exportGenerating: "生成中…",
      exportStartBtn: "开始生成导出文件",
      exportReady: "导出文件已就绪",
      exportJobId: "任务编号：",
      exportToken: "下载凭证：",
      exportExpires: "下载链接有效期 24 小时，过期将自动清理。",
      downloadFileBtn: "立即下载文件",
      closeBtn: "关闭",
      retry: "重试",
      reload: "重新加载",
      ruleVersion: "规则版本 ",
      pendingAggregation: "待聚合 ",
      watermark: "水位 ",
      provisional: "provisional (96h 未封定)",
      collectionMode: "模式 ",
      channelsLabel: "通道 ",
      agentStatus: "Agent 最近状态: ",
      knownGaps: "已知缺口 ",
      unknownLocation: "未知 / 局域网",
      emptyChart: "当前时段暂无样本数据。",
      qualityCoverage: "数据采集质量覆盖率: ",
      qualityNeverCollected: "（从未采集；数值不代表真实 0）",
      dangerZoneTitle: "危险区域",
      deleteSiteNotice: "删除站点将立即注销所有上报密钥，停止该站点的浏览器埋点与源站数据采集与分析查询。",
      deleteSiteBtn: "删除此站点",
      deleteSiteConfirmTitle: "删除站点",
      deleteSiteConfirmPrompt: "确定要永久删除此站点吗？删除后该站点的所有数据采集与分析查询将立即停止。",
      deleteSiteSuccess: "站点已成功删除。",
    },
    guide: {
      title: "网站接入与集成指引",
      subtitle: "提供面向开发者的极速集成指南，以及专为 AI 编程工具设计的一键 Prompt。",
      tabHuman: "📖 开发者指南 (人读版)",
      tabAi: "🤖 AI CODING 提示词 (AI 专用)",
      copyCode: "📋 复制代码",
      copiedCode: "✅ 已复制到剪贴板",
      generateKey: "⚡ 立即生成浏览器采集密钥",
      generatingKey: "生成中…",
      refreshKey: "🔄 刷新",
      siteKeyLabel: "公开密钥 (Public Site Key): ",
      keyStatusGenerated: "已生成生效",
      keyStatusPlaceholder: "尚未生成 (当前为占位符)",
      step1Title: "1. 快速接入（在 HTML <head> 插入单行代码）",
      step2Title: "2. 主流前端框架与 Nginx 反代方案",
      step3Title: "3. 架构优势与安全承诺",
      step4Title: "4. 接入核验与测试",
      feature1Title: "⚡ 100% 异步非阻塞 (对业务 0 影响)",
      feature1Desc: "必须通过 defer 异步加载；上报采用 sendBeacon 与 fetch keepalive 静默投递，沙箱异常静默吸收，绝不拖慢网页首次渲染与 LCP 评级。",
      feature2Title: "🛡️ Nginx 旁路与第一方反代物理隔离",
      feature2Desc: "支持反代同源模式，剥离客户业务 Cookie，防止广告插件误杀；后端即使维护，前端请求毫秒级处理丝毫不受波及。",
      feature3Title: "🚀 全自动 SPA 路由拦截",
      feature3Desc: "原生自动拦截 pushState、popstate 与 replaceState，单页切换自动记录 Pageview 与停留时长，无需手动在 Router 中编写钩子。",
      feature4Title: "📊 真实体验 Core Web Vitals",
      feature4Desc: "自动收集真实访客的 Google Web Vitals 核心指标：LCP、INP、CLS、FCP、TTFB，真实反映访客网络与渲染体感。",
      aiBannerTitle: "🤖 AI 编程工具极速集成 (One-Click AI Prompt)",
      aiBannerDesc: "专门针对 Cursor (Composer / Agent)、Claude Code、GitHub Copilot、Windsurf 优化。点击复制直接发给 AI，全自动识别技术栈并封装埋点。",
      copyAiPromptBtn: "📋 一键复制完整 AI Prompt",
      aiPreviewHeader: "Prompt 预览 (包含当前站点的真实 Site Key 与端点地址)",
    },
    account: {
      title: "账户安全与资料",
      emailLabel: "当前注册邮箱: ",
      emailVerified: "已验证",
      emailUnverified: "未验证",
      mfaSectionTitle: "两步验证 (MFA / TOTP)",
      mfaStatusEnabled: "已启用",
      mfaStatusDisabled: "未启用",
      mfaEnableBtn: "启用两步验证",
      mfaDisableBtn: "关闭两步验证",
      mfaScanQr: "请使用 Google Authenticator 或 1Password 扫描下方二维码：",
      mfaSecretLabel: "手动输入密钥: ",
      mfaConfirmCodeLabel: "输入应用中生成的 6 位动态验证码:",
      mfaConfirmBtn: "确认并激活 MFA",
      mfaRecoveryCodesTitle: "应急恢复码 (请妥善保存至安全位置):",
      deleteSectionTitle: "注销账户",
      deleteWarning: "账户注销不可撤销。若您名下有工作空间，请先转让所有权或删除工作空间。",
      deleteBtn: "注销当前账户",
      changePasswordTitle: "修改登录密码",
      changePasswordDesc: "定期更换密码以保障账户安全，新密码至少 12 位并包含多种字符。",
      currentPasswordLabel: "当前密码",
      newPasswordLabel: "新密码 (至少 12 位)",
      confirmNewPasswordLabel: "确认新密码",
      changePasswordBtn: "更新密码",
      changePasswordSuccess: "密码修改成功！除当前会话外，其他设备的登录已全部注销。",
      passwordMismatch: "两次输入的新密码不一致",
      passwordSameAsCurrent: "新密码不能与当前密码相同",
    },
  },
} as const;

export type TranslationKey = typeof translations.en;
